<script lang="ts">
	import {
		CardGrid,
		CircleAvatar,
		DashSelectCard,
		Text,
		Stack,
		LabelV2,
	} from '@Shelly/shelly-design-system';
	import type {
		DashCameraWidgetConfig,
		DashCameraWidgetVariant,
		DashSelectList,
		DashWidgetEditType,
		DashWidgetTypes,
		ListRefType,
	} from '../../dash-types';
	import { edit } from 'src/stores/ui/edit_lists';
	import { getWidgetTitleProposal } from '../../dash-helpers';
	import { getDefaultEnergyConfigForType } from '../../dash-energy-helpers';
	import {
		isDashEnergyWidgetType,
		type DashEnergyHeatmapVariant,
		type DashEnergyPeakVariant,
		type DashEnergyWidgetTypes,
	} from '../../dash-energy-types';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { rooms } from 'src/stores/lists/rooms';
	import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
	import { scenes } from 'src/stores/lists/scenes';
	import { filterScenesInitial } from 'src/components/Lists/list-helpers';
	import { checkLightGroupHasSingleSlider } from '../../dash-lights-helpers';
	import { getDashWidgetSelectListRefKey, normalizeCameraConfig } from '../../dash-camera-helpers';
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
	import { format } from 'svelte-i18n';
	import { hasCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	import { getDevicesWithEnvironmentReadings } from '../../dash-environment-helpers';
	import { isTestUser } from 'src/helpers/user_helpers';

	export let editItem: DashWidgetEditType;
	export let allCardsRefs: ListRefType = {};
	export let energyOnly = false;

	$: isPremium = $user_data.premium || $user_settings.been_premium;
	const showDevCameraWidgets = isTestUser();

	$: DASH_WIDGET_TYPES_LIST = [
		{
			title: $format('label_widget_devices', {
				default: 'Devices',
			}),
			icon: 'fal fa-microchip',
			selectCards: [
				// {
				// 	title: $format('label_devices_data_card', {
				// 		default: 'Data cards',
				// 	}),
				// 	additionalText: $format('label_devices_data_card_info', {
				// 		default: 'Displays device data only.',
				// 	}),
				// 	type: 'dashParameterDev',
				// 	disabled: Object.values($devices).length <= 0,
				// },
				{
					title: $format('label_devices_quick_card', {
						default: 'Control card',
					}),
					additionalText: $format('label_devices_quick_card_info', {
						default: 'Control the device by tapping anywhere on the card.',
					}),
					type: 'dashQuickDev',
					disabled: Object.values($devices).length <= 0,
				},
				// Button cards merged into Control card (Advanced → control placement).
				// {
				// 	title: $format('label_devices_card', {
				// 		default: 'Button card',
				// 	}),
				// 	additionalText: $format('label_devices_card_info', {
				// 		default: 'Control the device using the button.',
				// 	}),
				// 	type: 'dashCardDev',
				// 	disabled: Object.values($devices).length <= 0,
				// },
				{
					title: $format('label_devices_control_card', {
						default: 'Horizontal button card',
					}),
					additionalText: $format('label_devices_control_card_info', {
						default: 'Control the device using the button.',
					}),
					type: 'dashControlDev',
					disabled: Object.values($devices).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_rooms', {
				default: 'Rooms',
			}),
			icon: 'fal fa-door-open',
			selectCards: [
				{
					title: $format('label_rooms_data_card', {
						default: 'Rooms',
					}),
					additionalText: '',
					type: 'dashRoom',
					disabled: Object.values($rooms).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_groups', {
				default: 'Groups',
			}),
			icon: 'fal fa-microchip',
			selectCards: [
				{
					title: $format('label_groups_data_card', {
						default: 'Groups',
					}),
					additionalText: '',
					type: 'dashGroup',
					disabled: Object.values($groups).length <= 0,
				},
				{
					title: $format('label_light_groups_data_card', {
						default: 'Light groups',
					}),
					additionalText: '',
					type: 'dashLight',
					disabled:
						Object.values($groups)
							.filter((group: any) => group.type === 'light')
							.filter((lightGroup) => checkLightGroupHasSingleSlider(lightGroup)).length <=
						0,
				},
			],
		},
		{
			title: $format('label_widget_scenes', {
				default: 'Scenes',
			}),
			icon: 'fal fa-microchip',
			selectCards: [
				{
					title: $format('label_scenes_data_card', {
						default: 'Scenes',
					}),
					additionalText: '',
					type: 'dashScene',
					disabled: Object.values($scenes).filter(filterScenesInitial).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_alarms', {
				default: 'Alarms',
			}),
			icon: 'fal fa-siren-on',
			selectCards: [
				{
					title: $format('label_alarms_data_card', {
						default: 'Alarms',
					}),
					additionalText: '',
					type: 'dashAlarm',
					disabled: Object.values($alarms).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_thermostats', {
				default: 'Thermostats',
			}),
			icon: 'fal fa-temperature-half',
			selectCards: [
				{
					title: $format('label_thermostats_data_card', {
						default: 'Thermostats',
					}),
					type: 'dashThermostat',
					disabled: Object.values($temp_schemes).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_energy', {
				default: 'Energy',
			}),
			icon: 'fal fa-bolt',
			selectCards: [
				{
					title: $format('label_energy_data_card', {
						default: 'Energy',
					}),
					additionalText: $format('label_energy_data_card_overall_info', {
						default: 'Overall consumption chart.',
					}),
					type: 'dashChart',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_top_consumers_title', {
						default: 'Top consumers',
					}),
					additionalText: '',
					type: 'dashEnergyTopConsumers',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_consumer_mix_title', { default: 'Consumer mix' }),
					additionalText: '',
					type: 'dashEnergyConsumerMix',
					disabled: false,
				},
				{
					title: $format('dash_energy_room_chart_proposal', { default: 'Room consumption' }),
					additionalText: $format('dash_energy_room_chart_info', {
						default:
							'Historical chart for one room. Select multiple rooms to create separate widgets.',
					}),
					type: 'dashEnergyRoomChart',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_flow_title', { default: 'Energy flow' }),
					additionalText: $format('dash_energy_flow_info', {
						default: 'Sankey diagram of energy sources and consumers.',
					}),
					type: 'dashEnergyFlow',
					disabled: false,
				},
				{
					title: $format('dash_energy_heatmap_title', { default: 'Yearly Heatmap' }),
					additionalText: $format('dash_energy_heatmap_info', {
						default: 'Daily or hourly consumption and generation intensity.',
					}),
					type: 'dashEnergyHeatmap',
					heatmapVariant: 'full',
					image: './images/dash/dash-widget-dashEnergyHeatmap-full.svg',
					disabled: false,
				},
				{
					title: $format('dash_energy_heatmap_minimal_title', {
						default: 'Monthly Heatmap',
					}),
					additionalText: $format('dash_energy_heatmap_minimal_info', {
						default: 'Compact current-month intensity, cost, peak hour, and daily trend.',
					}),
					type: 'dashEnergyHeatmap',
					heatmapVariant: 'minimal',
					image: './images/dash/dash-widget-dashEnergyHeatmap-minimal.svg',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_peak_overview_title', {
						default: 'Peak Power Overview',
					}),
					additionalText: $format('energy_monitoring_peak_overview_info', {
						default: 'Highest live peak, 5-minute average, difference, and channel ranking.',
					}),
					type: 'dashEnergyPeak',
					peakVariant: 'overview',
					image: './images/dash/dash-widget-dashEnergyPeak-overview.svg',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_peak_by_meter_title', {
						default: 'Peak Power by Meter',
					}),
					additionalText: $format('energy_monitoring_peak_by_meter_info', {
						default: 'Compare the strongest live phase or channel across enabled meters.',
					}),
					type: 'dashEnergyPeak',
					peakVariant: 'byMeter',
					image: './images/dash/dash-widget-dashEnergyPeak-byMeter.svg',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_phase_peak_balance_title', {
						default: 'Phase Peak Balance',
					}),
					additionalText: $format('energy_monitoring_phase_peak_balance_info', {
						default: 'Compare A, B, and C peak snapshots on the strongest 3-phase meter.',
					}),
					type: 'dashEnergyPeak',
					peakVariant: 'phaseBalance',
					image: './images/dash/dash-widget-dashEnergyPeak-phaseBalance.svg',
					disabled: false,
				},
				{
					title: $format('settings_title_insights', { default: 'Live insights' }),
					additionalText: $format('energy_monitoring_insights_info', {
						default: 'Live solar coverage, grid reliance, and standby waste.',
					}),
					type: 'dashEnergyInsights',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_generation_title', {
						default: 'Generation vs consumption',
					}),
					additionalText: $format('energy_monitoring_generation_info', {
						default: 'Historical generation and consumption for the selected period.',
					}),
					type: 'dashEnergyGeneration',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_distribution_title', {
						default: 'Live energy distribution',
					}),
					additionalText: $format('energy_monitoring_distribution_info', {
						default: 'Live PV production, grid, and home energy distribution.',
					}),
					type: 'dashEnergyPvDistribution',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_tariff_title', { default: 'Tariff pressure' }),
					additionalText: $format('energy_monitoring_tariff_info', {
						default: 'Usage, cost, and exposure to configured tariff bands.',
					}),
					type: 'dashEnergyTariff',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_phase_health_title', { default: 'Phase health' }),
					additionalText: $format('energy_monitoring_phase_health_info', {
						default: 'Live balance and load across meter phases.',
					}),
					type: 'dashEnergyPhase',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_environment_title', {
						default: 'Environmental conditions',
					}),
					additionalText: $format('energy_monitoring_environment_info', {
						default: 'Environmental measurements from compatible devices.',
					}),
					type: 'dashEnergyEnvironmental',
					disabled: false,
				},
				{
					title: $format('energy_v3_device_trend_title', {
						default: 'Device energy trend',
					}),
					additionalText: $format('energy_v3_device_trend_info', {
						default: 'Energy history for a power-meter device.',
					}),
					type: 'dashEnergyDeviceTrend',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_room_chart_title', {
						default: 'Room consumption overview',
					}),
					additionalText: $format('energy_monitoring_room_charts_info', {
						default: 'Charts for all rooms with power-meter devices.',
					}),
					type: 'dashEnergyRoomCharts',
					disabled: false,
				},
				{
					title: $format('energy_monitoring_room_distribution_title', {
						default: 'Energy Distribution By Room',
					}),
					additionalText: $format('energy_monitoring_room_distribution_info', {
						default:
							'Horizontal comparison of total consumption across every room for the selected period.',
					}),
					type: 'dashEnergyRoomDistribution',
					disabled: false,
				},
			],
		},
		{
			title: $format('label_widget_info', {
				default: 'Info',
			}),
			icon: 'fal fa-circle-info',
			selectCards: [
				{
					title: $format('dash_energy_info_widget_proposal', { default: 'Info widget' }),
					additionalText: $format('dash_energy_info_widget_info', {
						default: 'Choose live consumption, generation, cost, and battery metrics.',
					}),
					type: 'dashEnergyInfo',
					image: './images/dash/dash-widget-dashEnergyInfo.svg',
					disabled: false,
				},
			],
		},
		{
			title: $format('label_widget_cameras', {
				default: 'Cameras',
			}),
			icon: 'fal fa-video',
			selectCards: [
				{
					title: $format('label_camera_widget_card', {
						default: 'Camera widget',
					}),
					additionalText: $format('label_camera_widget_card_info', {
						default: 'Add up to four cameras. Layout follows how many you pick.',
					}),
					type: 'dashCamera',
					cameraVariant: 'auto',
					image: './images/dash/dash-widget-dashCamera.svg',
					bottomLabelIcon: 'fal fa-video',
					disabled: !hasCameraDevices($devices),
				},
				{
					title: $format('label_camera_single_card', {
						default: '1 Camera',
					}),
					additionalText: $format('label_camera_single_card_info', {
						default: 'One player spanning the column.',
					}),
					type: 'dashCamera',
					cameraVariant: 'single',
					image: './images/dash/dash-widget-dashCamera-single.svg',
					bottomLabelIcon: 'fal fa-video',
					disabled: !hasCameraDevices($devices),
					devOnly: true,
				},
				{
					title: $format('label_camera_pair_card', {
						default: '2 Camera',
					}),
					additionalText: $format('label_camera_pair_card_info', {
						default: 'Two players in one row, including on mobile.',
					}),
					type: 'dashCamera',
					cameraVariant: 'pair',
					image: './images/dash/dash-widget-dashCamera-pair.svg',
					bottomLabelIcon: 'fal fa-video',
					disabled: !hasCameraDevices($devices),
					devOnly: true,
				},
				{
					title: $format('label_camera_triple_focus_card', {
						default: '3 Camera Layout',
					}),
					additionalText: $format('label_camera_triple_focus_card_info', {
						default: 'Focus one camera with two smaller previews.',
					}),
					type: 'dashCamera',
					cameraVariant: 'triple-focus',
					image: './images/dash/dash-widget-dashCamera-triple-focus.svg',
					bottomLabelIcon: 'fal fa-video',
					disabled: !hasCameraDevices($devices),
					devOnly: true,
				},
				{
					title: $format('label_camera_quad_card', {
						default: '4 Camera',
					}),
					additionalText: $format('label_camera_quad_card_info', {
						default: 'Two rows of two players.',
					}),
					type: 'dashCamera',
					cameraVariant: 'quad',
					image: './images/dash/dash-widget-dashCamera-quad.svg',
					bottomLabelIcon: 'fal fa-video',
					disabled: !hasCameraDevices($devices),
					devOnly: true,
				},
			],
		},
		{
			title: $format('label_widget_environment', {
				default: 'Environment',
			}),
			icon: 'fal fa-temperature-half',
			selectCards: [
				{
					title: $format('label_environment_data_card', {
						default: 'Temperature & humidity',
					}),
					additionalText: $format('label_environment_data_card_info', {
						default: 'Show temperature and humidity from selected sensors and devices.',
					}),
					type: 'dashEnvironment',
					disabled: getDevicesWithEnvironmentReadings(Object.values($devices)).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_special', {
				default: 'Special',
			}),
			icon: 'fak fa-light-sparkles',
			selectCards: [
				{
					title: $format('label_special_acc_info_data_card', {
						default: 'Account Information',
					}),
					additionalText: '',
					type: 'dashInfo',
					bottomLabelIcon: 'fal fa-user',
					disabled: false,
				},
				{
					title: $format('label_special_weather_data_card', {
						default: 'Weather',
					}),
					additionalText: '',
					type: 'dashWeather',
					bottomLabelIcon: 'fal fa-clouds',
					disabled: !isPremium,
					premium: true,
				},
				{
					title: $format('label_special_music_data_card', {
						default: 'Music Player',
					}),
					additionalText: '',
					type: 'dashMedia',
					bottomLabelIcon: 'fal fa-music',
					disabled:
						Object.values($devices).filter((device) => device?.category === 'media_player')
							.length <= 0,
				},
			],
		},
	] as DashSelectList;

	$: visibleWidgetTypes = energyOnly
		? DASH_WIDGET_TYPES_LIST.map((section) => ({
				...section,
				selectCards: section.selectCards.filter((card) => isDashEnergyWidgetType(card.type)),
			})).filter((section) => section.selectCards.length > 0)
		: DASH_WIDGET_TYPES_LIST;

	/** Config to keep for the newly selected type; drops what no longer applies. */
	const nextCameraConfig = (
		type: DashWidgetTypes,
		cameraVariant?: DashCameraWidgetVariant,
	): DashCameraWidgetConfig | undefined => {
		const persisted = normalizeCameraConfig(editItem.cameraConfig);

		if (type === 'dashCamera') {
			const isSameCameraVariant =
				editItem.cameraConfig?.variant === cameraVariant ||
				(cameraVariant === 'auto' &&
					(editItem.cameraConfig?.variant === 'full-preview' ||
						editItem.cameraConfig?.variant === 'auto' ||
						!editItem.cameraConfig?.variant ||
						(editItem.cameraConfig?.variant === 'triple-focus' && !showDevCameraWidgets)));
			return {
				variant: isSameCameraVariant
					? editItem.cameraConfig?.variant || cameraVariant || 'auto'
					: cameraVariant || 'auto',
				feedMode: isSameCameraVariant ? persisted.feedMode : 'live',
				sideFeedMode:
					isSameCameraVariant && persisted.sideFeedMode ? persisted.sideFeedMode : 'live',
				mainAlwaysLive: true,
			};
		}

		// Device card widgets carry a feed choice but no layout. Keep one the user
		// already made; don't invent one, since reads default to preview.
		if ((type === 'dashCardDev' || type === 'dashQuickDev') && editItem.cameraConfig) {
			return {
				feedMode: persisted.feedMode,
				sideFeedMode: persisted.sideFeedMode,
				mainAlwaysLive: true,
			};
		}

		return undefined;
	};

	const onSelectWidgetType = (
		type: DashWidgetTypes,
		cameraVariant?: DashCameraWidgetVariant,
		heatmapVariant?: DashEnergyHeatmapVariant,
		peakVariant?: DashEnergyPeakVariant,
	) => {
		editItem.selectItems = [];
		const currentHeatmapVariant = editItem.energyConfig?.heatmapVariant ?? 'full';
		const currentPeakVariant = editItem.energyConfig?.peakVariant ?? 'overview';

		if (
			!$edit ||
			editItem.widgetType !== type ||
			editItem.cameraConfig?.variant !== cameraVariant ||
			(type === 'dashEnergyHeatmap' && currentHeatmapVariant !== heatmapVariant) ||
			(type === 'dashEnergyPeak' && currentPeakVariant !== peakVariant)
		) {
			editItem.widgetTitle =
				type === 'dashEnergyHeatmap' && heatmapVariant === 'minimal'
					? $format('dash_energy_heatmap_minimal_title', { default: 'Monthly Heatmap' })
					: type === 'dashEnergyPeak' && peakVariant === 'byMeter'
						? $format('energy_monitoring_peak_by_meter_title', {
								default: 'Peak Power by Meter',
							})
						: type === 'dashEnergyPeak' && peakVariant === 'phaseBalance'
							? $format('energy_monitoring_phase_peak_balance_title', {
									default: 'Phase Peak Balance',
								})
							: getWidgetTitleProposal(type);
		}

		editItem.cameraConfig = nextCameraConfig(type, cameraVariant);
		editItem.widgetType = type;

		if (type !== 'dashQuickDev') {
			editItem.deviceCardConfig = undefined;
		}

		if (isDashEnergyWidgetType(type)) {
			editItem.energyConfig = getDefaultEnergyConfigForType(type as DashEnergyWidgetTypes);
			if (type === 'dashEnergyHeatmap') {
				editItem.energyConfig = {
					...editItem.energyConfig,
					heatmapVariant: heatmapVariant ?? 'full',
					allowInWidgetMetricToggle:
						heatmapVariant === 'minimal'
							? false
							: editItem.energyConfig.allowInWidgetMetricToggle,
				};
			} else if (type === 'dashEnergyPeak') {
				editItem.energyConfig = {
					...editItem.energyConfig,
					peakVariant: peakVariant ?? 'overview',
				};
			}
		} else {
			editItem.energyConfig = undefined;
		}
		if (type === 'dashChart') {
			editItem.selectItems = [
				{
					id: 'overalConsumption',
					itemType: 'chart',
				},
			];
		} else if (type === 'dashWeather') {
			editItem.selectItems = [
				{
					id: 'weatherWidget',
					itemType: 'weather',
				},
			];
		} else if (type === 'dashInfo') {
			editItem.selectItems = [
				{
					itemType: 'acc_info',
					id: 'consumption',
				},
				{
					itemType: 'acc_info',
					id: 'allDevices',
				},
				{
					itemType: 'acc_info',
					id: 'offlineDevices',
				},
				{
					itemType: 'acc_info',
					id: 'sharedDevices',
				},
				{
					itemType: 'acc_info',
					id: 'recentlySharedDevices',
				},
				{
					itemType: 'acc_info',
					id: 'hiddenDevices',
				},
			];
		}
	};
</script>

<Stack gap="l" direction="row">
	{#each visibleWidgetTypes as item (item.title)}
		<div>
			<div class="d-flex align-items-center gap-s mb-m">
				<CircleAvatar
					size="m"
					backgroundColor="color-primary-10"
					icon={`${item.icon} text-color-primary`}
				/>
				<Text styleType="controls-large" class="text-800">{item.title}</Text>
			</div>

			<CardGrid lg={210} sm={160} gridGap="s" noPaddingBottom>
				{#each item.selectCards.filter((card) => !card.devOnly || showDevCameraWidgets) as card (`${card.type}-${card.cameraVariant || 'default'}`)}
					{@const cardRefKey = getDashWidgetSelectListRefKey(
						card.type,
						card.cameraVariant,
						card.heatmapVariant,
						card.peakVariant,
					)}
					{@const isCameraSelected =
						card.type === 'dashCamera' &&
						editItem.widgetType === 'dashCamera' &&
						(card.cameraVariant === 'auto'
							? !editItem.cameraConfig?.variant ||
								editItem.cameraConfig?.variant === 'auto' ||
								editItem.cameraConfig?.variant === 'full-preview' ||
								(editItem.cameraConfig?.variant === 'triple-focus' &&
									!showDevCameraWidgets)
							: editItem.cameraConfig?.variant === card.cameraVariant)}
					<div bind:this={allCardsRefs[cardRefKey]}>
						<DashSelectCard
							isFocusable
							class="flex-1 min-width-0"
							title={card.title}
							additionalText={card.additionalText}
							icon={card.icon}
							image={card.image ||
								(!card.icon ? `./images/dash/dash-widget-${card.type}.svg` : undefined)}
							hideBottomLabelSlot={!card.bottomLabelIcon}
							selected={card.type === 'dashCamera'
								? isCameraSelected
								: card.type === 'dashEnergyHeatmap'
									? editItem.widgetType === card.type &&
										(editItem.energyConfig?.heatmapVariant ?? 'full') ===
											(card.heatmapVariant ?? 'full')
									: card.type === 'dashEnergyPeak'
										? editItem.widgetType === card.type &&
											(editItem.energyConfig?.peakVariant ?? 'overview') ===
												(card.peakVariant ?? 'overview')
										: card.type === editItem.widgetType}
							disabled={card.disabled}
							on:click={() => {
								onSelectWidgetType(
									card.type,
									card.cameraVariant,
									card.heatmapVariant,
									card.peakVariant,
								);
							}}
							hideTopLeftLabelSlot={!card.premium && !card.devOnly}
						>
							<LabelV2
								slot="topLeftLabelSlot"
								size="s"
								icon={card.premium ? 'fal fa-gem' : 'fal fa-code'}
								noRadiusType="second"
								value={card.premium
									? $format('premium', { default: 'Premium' })
									: $format('label_dev_only', { default: 'Dev' })}
								backgroundColor={card.premium ? '--gradient-premium' : '--color-2'}
							/>

							<LabelV2
								slot="bottomLabelSlot"
								size="s"
								icon={mapIconSolidWeightToLight(card.bottomLabelIcon)}
								noRadiusType="second"
								backgroundColor="--color-2"
							/>
						</DashSelectCard>
					</div>
				{/each}
			</CardGrid>
		</div>
	{/each}
</Stack>
